import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import GetCookie from '../utils/GetCookie';
import ErrorPopup from '../utils/popup/ErrorPopup';
import { useTimezone } from '../utils/TimezoneContext';

const NotificationDropdown = ({ user_type }) => {
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');
  const dropdownRef = useRef(null);
  const accessToken = GetCookie('access_token');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Updated fetchNotifications function to use dynamic endpoint based on user_type
  const fetchNotifications = async (page = 1, append = false) => {
    if (loading) return;

    setLoading(true);

    try {
      // Determine the correct endpoint based on user_type prop
      const getEndpoint = (userType) => {
        switch (userType?.toLowerCase()) {
          case 'admin':
            return '/pceapi/notification/administration/list';
          case 'iam':
          default:
            return '/pceapi/notification/iam/list';
        }
      };

      const endpoint = getEndpoint(user_type);

      const response = await axios.post(endpoint, {
        page: page,
        size: 10
      }, {
        headers: {
          Authorization: accessToken,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.status === 'success') {
        const newNotifications = response.data.data;
        const totalFromResponse = response.data.total || 0;

        if (append) {
          setNotifications(prev => [...prev, ...newNotifications]);
        } else {
          setNotifications(newNotifications);
        }

        setTotalCount(totalFromResponse);
        setHasMore(newNotifications.length === 10 && (page * 10) < totalFromResponse);
      } else {
        // Handle API error response consistently
        setPopupErrorMessage(response.data.message || 'Failed to fetch notifications');
        setIsErrorPopupVisible(true);
        console.error('Failed to fetch notifications:', response.data.message);
      }
    } catch (error) {
      // Handle network/server errors consistently
      setPopupErrorMessage('Error connecting to server');
      setIsErrorPopupVisible(true);
      console.error(`Error fetching notifications for ${user_type}:`, error);
    } finally {
      setLoading(false);
    }
  };

  // Load more notifications
  const loadMore = () => {
    if (!loading && hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchNotifications(nextPage, true);
    }
  };

  // Toggle dropdown and fetch notifications
  const toggleDropdown = () => {
    if (!isOpen) {
      setCurrentPage(1);
      fetchNotifications(1, false);
    }
    setIsOpen(!isOpen);
  };

  // Close error popup
  const closeErrorPopup = () => {
    setIsErrorPopupVisible(false);
    setPopupErrorMessage('');
  };

  // Format date
  const { formatRelativeTime } = useTimezone();

  // Get notification icon based on action
  const getNotificationIcon = (action) => {
    const actionKey = action?.toLowerCase().replace(/\s+/g, '_');
    const actionIcons = {
      created: 'bi-plus-circle-fill',
      updated: 'bi-pencil-square',
      deleted: 'bi-trash-fill',
    };

    return actionIcons[actionKey] || 'bi-bell-fill';
  };

  // Get notification color based on action
  const getNotificationColor = (action) => {
    const actionKey = action?.toLowerCase().replace(/\s+/g, '_');
    const actionColors = {
      created: '#4caf50',
      updated: '#2196f3',
      deleted: '#f44336',
    };

    return actionColors[actionKey] || '#1976d2'; // Default blue color
  };

  // Get action-specific badge styling
  const getActionBadgeStyle = (action) => {
    const actionKey = action?.toLowerCase().replace(/\s+/g, '_');

    const actionStyles = {
      created: { backgroundColor: '#4caf50' },
      updated: { backgroundColor: '#2196f3' },
      deleted: { backgroundColor: '#f44336' },
    };

    return actionStyles[actionKey] || { backgroundColor: '#757575' }; // Default gray
  };

  return (
    <>
      <div className="notification-dropdown ms-3" ref={dropdownRef}>
        <button
          className="ev-btn-lightblue-fill-round nav-round-btn avatar-tooltip"
          onClick={toggleDropdown}
          data-tooltip="Notifications"

        >
          <i className="bi bi-bell-fill"></i>
          {/*
          {totalCount > 0 && (
            <span className="notification-badge badge badge-sm bg-danger">
              {totalCount > 99 ? '99+' : totalCount}
            </span>
          )}
          */}
        </button>

        {isOpen && (
          <div className="notification-dropdown-menu">
            <div className="notification-header">
              <p className="ev-reg-p-dark mb-1">Notifications ({totalCount})</p>
              <button
                className="ev-btn-light-fill"
                onClick={() => setIsOpen(false)}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="scrollable-element vh-40" >
              {loading && notifications.length === 0 ? (
                <div className="text-center p-5 mt-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : notifications.length === 0 ? (
                <div className="text-center p-3 mt-5">
                  <i className="bi bi-bell-slash"></i>
                  <p className="ev-small-p-blue">0 Notifications Found</p>
                </div>
              ) : (
                <>
                  {notifications.map((notification) => {
                    const actionColor = getNotificationColor(notification.action);
                    const actionBadgeStyle = getActionBadgeStyle(notification.action);

                    // Logic to Create New Name (You already have this)
                    let displayType = notification.type;
                    if (displayType && displayType.toLowerCase() === 'fabric') {
                      displayType = 'PROJECT';
                    }

                    // Logic to Create New Message (You already have this)
                    let displayMessage = notification.message;
                    if (displayMessage) {
                      displayMessage = displayMessage.replace(/Fabric/i, 'Project');
                    }

                    return (
                      <div key={notification.id} className="notification-item">
                        <div
                          className="notification-icon"
                          style={{
                            backgroundColor: actionColor + '20',
                            color: actionColor,
                            border: `2px solid ${actionColor}30`
                          }}
                        >
                          <i className={`bi ${getNotificationIcon(notification.action)}`}></i>
                        </div>
                        <div>
                          {/* FIX 1: Use displayMessage here, NOT notification.message */}
                          <div className="ev-xsmall-p-dark mb-2">{displayMessage}</div>

                          <div className="notification-meta">
                            <span className="ev-xsmall-p-dark">{formatRelativeTime(notification.created_at)}</span>
                            <div className="notification-badges">
                              <span
                                className="notification-action-badge bg-primary"
                              >
                                {/* FIX 2: Use displayType here, NOT notification.type */}
                                {displayType}
                              </span>
                              <span
                                className="notification-action-badge ms-2"
                                style={actionBadgeStyle}
                              >
                                {notification.action}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {hasMore && (
                    <div className="ms-3 mt-1">
                      <button
                        className="ev-btn-blue-outline-round ev-bold-reg ev-btn-small"
                        onClick={loadMore}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <div className="loading-spinner small"></div>
                            Loading...
                          </>
                        ) : (
                          'Load More'
                        )}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {notifications.length > 0 && (
              <div className="notification-footer">
                {/* <button className="view-all-btn">
                  View All Notifications
                </button> */}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error Popup */}
      {isErrorPopupVisible && (
        <ErrorPopup
          onClose={closeErrorPopup}
          message={popupErrorMessage}
        />
      )}
    </>
  );
};

export default NotificationDropdown;